import { useId } from 'react';
import { Info } from '@phosphor-icons/react';
import {
  Area,
  ComposedChart,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
} from 'recharts';

import { CardErrorState } from './CardErrorState';
import { InfoTooltip } from './InfoTooltip';
import pt from '../../locales/pt';
import { formatEUR } from '../../utils/money';
import { cn } from '../../utils/cn';
import { chartTooltipStyle, useChartTheme } from '../../utils/chartTheme';
import { useTheme } from '../../store/ThemeContext';

/**
 * `.pen` → `ds/chart/area` (`AQ6R9`) — master único parametrizável.
 *
 * O `.pen` modela duas sub-formas do MESMO componente (não dois componentes):
 *  - `markers:all` + `hint` + `foot` → evolução com pontos navegáveis
 *    (`Património · Normal`, instâncias `i34AL` / `j0rzd` / `BXVwn`)
 *  - `markers:last` + bloco direito (valor + chip de variação) + `foot:off`
 *    (`ds/chart/area · markers:last`, `fvS69`)
 *
 * Estrutura lida do master (`Get(..., {resolveVariables:true})`):
 *   casca  rounded-ds-lg border border-border bg-card p-space-5 gap-space-4
 *   top    flex justify-between gap-space-3 · tx (type-section + type-label-meta)
 *          · hint (pílula bg-muted) OU bloco direito (valor + chip success)
 *   plot   170px, 4 gridlines h-px bg-border (y 40/75/110/145), área com
 *          gradiente $sage-glow→transparente, linha sage-500 2.5px,
 *          dots 10px com anel 2px bg-card, marker final 14px sage-600 + glow 26px
 *   error  bloco de 170px entre `plot` e `axis`
 *   axis   flex justify-between type-caption muted
 *   foot   border-t border-border pt-space-3 gap-space-2 items-center
 */

const PLOT_HEIGHT = 170;
/** `.pen` `grid` — 4 linhas a y = 40 / 75 / 110 / 145 num plot de 170px. */
const GRID_Y = [40, 75, 110, 145];
const PLOT_STYLE = { height: PLOT_HEIGHT };

function AreaPlot({
  data,
  markers,
  onPointSelect,
  referenceValue,
  referenceLabel,
  valueFormatter,
  tooltipLabel,
  plotTestId,
  plotLabel,
}) {
  const chart = useChartTheme();
  const { resolvedTheme } = useTheme();
  const rawId = useId();
  const areaGradientId = `area-line-chart-fill-${rawId.replace(/:/g, '')}`;
  const lastIndex = data.length - 1;
  const interactive = typeof onPointSelect === 'function';

  const renderDot = (dotProps, active) => {
    const isLast = dotProps.index === lastIndex;
    if (markers === 'last' && !isLast) return null;
    const radius = isLast ? 7 : 5;
    const id = dotProps.payload?.id;
    return (
      <g key={`area-line-point-${active ? 'active-' : ''}${id ?? dotProps.index}`}>
        {isLast ? (
          <circle
            cx={dotProps.cx}
            cy={dotProps.cy}
            r={13}
            fill={chart.primary}
            opacity={0.35}
            style={{ filter: 'blur(6px)' }}
            aria-hidden
          />
        ) : null}
        <circle
          data-testid={id ? `area-line-point-${id}` : undefined}
          cx={dotProps.cx}
          cy={dotProps.cy}
          r={active ? radius + 2 : radius}
          fill={isLast ? chart.primaryDark : chart.primary}
          stroke={chart.tooltipBg}
          strokeWidth={2}
          className={interactive ? 'cursor-pointer' : undefined}
          onClick={interactive && id ? () => onPointSelect(id) : undefined}
        />
      </g>
    );
  };

  return (
    <div
      className="relative w-full"
      style={PLOT_STYLE}
      data-testid={plotTestId}
      role={plotLabel ? 'img' : undefined}
      aria-label={plotLabel}
    >
      <div className="pointer-events-none absolute inset-0" aria-hidden>
        {GRID_Y.map((y) => (
          <span
            key={y}
            className="absolute left-0 right-0 h-px bg-border"
            style={{ top: y }}
          />
        ))}
      </div>
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart
          accessibilityLayer={false}
          data={data}
          margin={{ top: 10, right: 8, left: 8, bottom: 8 }}
        >
          <defs>
            <linearGradient id={areaGradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={chart.primary} stopOpacity={0.35} />
              <stop offset="100%" stopColor={chart.primary} stopOpacity={0} />
            </linearGradient>
          </defs>
          <Tooltip
            cursor={{ stroke: chart.axisLine }}
            contentStyle={chartTooltipStyle(resolvedTheme)}
            formatter={(value) => [valueFormatter(value), tooltipLabel]}
          />
          <Area
            type="monotone"
            dataKey="value"
            stroke="none"
            fill={`url(#${areaGradientId})`}
            isAnimationActive={false}
            dot={false}
            activeDot={false}
          />
          <Line
            type="monotone"
            dataKey="value"
            stroke={chart.primary}
            strokeWidth={2.5}
            strokeLinecap="round"
            strokeLinejoin="round"
            isAnimationActive={false}
            dot={(dotProps) => renderDot(dotProps, false)}
            activeDot={(dotProps) => renderDot(dotProps, true)}
          />
          {referenceValue > 0 ? (
            <ReferenceLine
              y={referenceValue}
              stroke={chart.axis}
              strokeDasharray="5 3"
              strokeWidth={1.5}
              label={
                referenceLabel
                  ? {
                      value: referenceLabel,
                      position: 'insideBottomRight',
                      fontSize: 11,
                      fill: chart.axis,
                      dy: -4,
                    }
                  : undefined
              }
            />
          ) : null}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

export function AreaLineChart({
  title,
  subtitle,
  hint = null,
  summary = null,
  data = [],
  axisLabels,
  markers = 'all',
  onPointSelect,
  foot = null,
  referenceValue = 0,
  referenceLabel,
  state = 'default',
  emptyLabel,
  errorLabel,
  onRetry,
  valueFormatter = formatEUR,
  tooltipLabel = '',
  plotTestId,
  plotLabel,
  variant = 'card',
  className,
  ...rest
}) {
  const hasHead = Boolean(title || subtitle || hint || summary);
  const HintIcon = hint?.icon;
  const DeltaIcon = summary?.delta?.icon;
  const labels = axisLabels ?? data.map((row) => row.label);

  const shellClass = cn(
    'flex w-full min-w-0 flex-col gap-space-4',
    variant === 'card'
      && 'rounded-ds-lg border border-border bg-card p-space-5 shadow-[0_6px_16px] shadow-glass-shadow/[0.12] dark:shadow-glass-shadow/[0.45]',
    className,
  );

  const head = hasHead ? (
    <div className="flex flex-wrap items-start justify-between gap-space-3">
      <div className="flex min-w-0 flex-col gap-space-1">
        {title ? (
          <p className="type-section text-muted-foreground">{title}</p>
        ) : null}
        {subtitle ? (
          <p className="type-label-meta text-muted-foreground">{subtitle}</p>
        ) : null}
      </div>

      {hint ? (
        <span className="type-chip inline-flex shrink-0 items-center gap-space-1 rounded-ds-full border border-border bg-muted px-space-2 py-space-1 font-semibold text-muted-foreground">
          {HintIcon ? <HintIcon className="h-3 w-3" aria-hidden /> : null}
          {hint.label}
        </span>
      ) : null}

      {summary ? (
        <div className="flex shrink-0 flex-col items-end gap-space-1">
          <span className="type-display-metric text-foreground">
            {summary.value}
          </span>
          {summary.delta ? (
            <span
              className={cn(
                'type-chip inline-flex items-center gap-space-1 rounded-ds-full px-space-2-5 py-space-1 font-semibold',
                summary.delta.tone === 'warning'
                  ? 'bg-warning-tint text-warning-fg'
                  : 'bg-success-tint text-success-fg',
              )}
            >
              {DeltaIcon ? <DeltaIcon className="h-3.5 w-3.5" aria-hidden /> : null}
              {summary.delta.label}
            </span>
          ) : null}
        </div>
      ) : null}
    </div>
  ) : null;

  const axis = labels.length ? (
    <div className="type-caption flex justify-between gap-space-2 text-muted-foreground">
      {labels.map((label, index) => (
        <span key={`${label}-${index}`} className="truncate">
          {label}
        </span>
      ))}
    </div>
  ) : null;

  const footBlock = foot ? (
    <p className="type-label-meta flex items-center justify-center gap-space-2 border-t border-border pt-space-3 text-muted-foreground">
      <Info className="h-3.5 w-3.5 shrink-0" aria-hidden />
      {foot.text}
      {foot.tooltip ? (
        <InfoTooltip text={foot.tooltip} className="h-3.5 w-3.5" />
      ) : null}
    </p>
  ) : null;

  if (state === 'loading') {
    return (
      <section className={shellClass} aria-busy="true" {...rest}>
        {head}
        <div
          className="w-full animate-pulse rounded-ds-lg bg-skeleton"
          style={PLOT_STYLE}
        />
      </section>
    );
  }

  if (state === 'error') {
    return (
      <section className={shellClass} {...rest}>
        {head}
        <CardErrorState
          title={errorLabel || pt.common.charts.evolutionError}
          onRetry={onRetry}
          className="justify-center"
          style={PLOT_STYLE}
        />
      </section>
    );
  }

  if (state === 'empty' || data.length < 2) {
    return (
      <section className={shellClass} {...rest}>
        {head}
        <p
          className="type-body flex items-center justify-center text-center text-muted-foreground"
          style={PLOT_STYLE}
        >
          {emptyLabel}
        </p>
      </section>
    );
  }

  return (
    <section className={shellClass} {...rest}>
      {head}
      <AreaPlot
        data={data}
        markers={markers}
        onPointSelect={onPointSelect}
        referenceValue={referenceValue}
        referenceLabel={referenceLabel}
        valueFormatter={valueFormatter}
        tooltipLabel={tooltipLabel}
        plotTestId={plotTestId}
        plotLabel={plotLabel}
      />
      {axis}
      {footBlock}
    </section>
  );
}

export default AreaLineChart;
